import { Exercise } from '../../../entities/exercise';
import { Delays } from '../../../entities/exercise/delays';
import { WorkoutSettings } from '../../../entities/user';
import { IAudioTip, Phase, SessionSequenceEntry } from '../../../entities/workoutsession/sessionsequenceentry';
import { UserExerciseSession } from '../../../entities/workoutsession/userexercisesession';
import { SessionSequenceStrategyContext } from '../../sessionsequence/sessionsequencestrategycontext';
import { getInstructorVoiceTip, getTechniqueVoiceTipFilePath } from '../../sessionsequence/utils';
import { assignForRest, IMotiTipStrategy } from './motiTipContext';

export class ThreeMotiTipsPerExercise implements IMotiTipStrategy {
    public assignVoiceTips(
        entry: SessionSequenceEntry,
        session: UserExerciseSession,
        settings: WorkoutSettings,
        motiTips: { Q1: Exercise[] | undefined; Q2: Exercise[] | undefined; Q3: Exercise[] | undefined },
        isStreamingWorkout: boolean) {
        const isBodyWeight = Exercise.isBodyWeightExercise(session.exercise);
        const voiceTipDelays = SessionSequenceStrategyContext.getVoiceTipsDelays(session.format,
             isBodyWeight, entry.durationInMilliseconds) as Delays;
        if (entry.videoHasAudio) {
            return entry;
        }
        if (!entry.audioTips) {
            entry.audioTips = [];
        }

        switch (entry.phase) {
            case Phase.Train:
                entry.audioTips = this.assignForTrain(
                    entry.audioTips, voiceTipDelays, settings, motiTips,
                    entry.techniqueTipAvailInstructors, entry.videoFile, isStreamingWorkout);
                break;
            case Phase.Rest: // instruction tip
                entry.audioTips = assignForRest(entry.videoFile, entry.audioTips, voiceTipDelays, settings);
                break;
        }

        return entry;
    }

    private shouldPlayTechniqueTip(
        settings: WorkoutSettings,
        techniqueTipAvailInstructors: string[] | undefined): boolean {
        if (!settings) {
            return false;
        }
        if (!settings.homeWorkoutInstructor) {
            return false;
        }
        if (!techniqueTipAvailInstructors || techniqueTipAvailInstructors.length === 0) {
            return false;
        }

        if (!techniqueTipAvailInstructors.includes(settings.homeWorkoutInstructor)) {
            return false;
        }
        return true;
    }

    private assignForTrain(
        audioTips: IAudioTip[],
        voiceTipDelays: Delays,
        settings: WorkoutSettings,
        motiTips: { Q1: Exercise[] | undefined; Q2: Exercise[] | undefined; Q3: Exercise[] | undefined },
        techniqueTipAvailInstructors: string[] | undefined,
        id: string,
        isStreamingWorkout: boolean) {
        const motivQ1 = motiTips.Q1 && !isNaN(voiceTipDelays.firstTipDelay) && motiTips.Q1.shift();

        if (isStreamingWorkout) {
          return [];
        }

        if (this.shouldPlayTechniqueTip(settings, techniqueTipAvailInstructors) && settings.homeWorkoutInstructor) {
            audioTips = [
                ...audioTips, {
                    fileName: getTechniqueVoiceTipFilePath(id, settings.homeWorkoutInstructor),
                    delay: voiceTipDelays.firstTipDelay,
                }];
        } else {
            if (motivQ1) {
                audioTips = [
                    ...audioTips, {
                        fileName: getInstructorVoiceTip(motivQ1, settings),
                        delay: voiceTipDelays.firstTipDelay,
                    }];
            }
        }

        const motivQ2 = motiTips.Q2 && !isNaN(voiceTipDelays.secondTipDelay) && motiTips.Q2.shift();
        if (motivQ2) {
            audioTips = [
                ...audioTips, {
                    fileName: getInstructorVoiceTip(motivQ2, settings),
                    delay: voiceTipDelays.secondTipDelay,
                }];
        }

        const motivQ3 = motiTips.Q3 && !isNaN(voiceTipDelays.thirdTipDelay) && motiTips.Q3.shift();
        if (motivQ3) {
            audioTips = [
                ...audioTips, {
                    fileName: getInstructorVoiceTip(motivQ3, settings),
                    delay: voiceTipDelays.thirdTipDelay,
                }];
        }
        return audioTips;
    }
}
